import React, { useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellRing, ChevronDown, ChevronUp, FileDown, Mail, MessageCircle } from "lucide-react";
import PaymentBadge from "@/components/PaymentBadge";
import { sourceLabel } from "@/components/admin/ReportsTab";
import OrderRowDetails from "@/components/admin/OrderRowDetails";

const FILTERS = ["all", "paid", "pending", "failed", "review"];
const ORDER_STATUSES = ["pending_payment", "payment_failed", "confirmed", "processing", "shipped", "delivered", "cancelled"];

const matchFilter = (o, f) => {
  if (f === "all") return true;
  if (f === "review") return Boolean(o.needs_review) && o.payment_status !== "paid";
  return o.payment_status === f;
};

export const OrdersTab = ({ orders, refresh }) => {
  const [filter, setFilter] = useState("all");
  const [source, setSource] = useState("all");
  const [expanded, setExpanded] = useState({});
  const counts = FILTERS.reduce((acc, f) => ({ ...acc, [f]: orders.filter((o) => matchFilter(o, f)).length }), {});
  const sources = Array.from(new Set(orders.map((o) => o.src || "direct"))).sort();
  const visible = orders.filter(
    (o) => matchFilter(o, filter) && (source === "all" || (o.src || "direct") === source)
  );
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const setStatus = async (id, status) => {
    await api.put(`/orders/${id}/status`, { status });
    refresh();
  };

  const resendInvoice = async (id) => {
    try {
      const { data } = await api.post(`/orders/${id}/resend-invoice`);
      toast.success(`Invoice emailed to ${data.to}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const retryFailedInvoices = async () => {
    try {
      const { data } = await api.post("/admin/invoices/retry-failed");
      toast.success(`Retried ${data.attempted} invoice(s) — ${data.sent} sent, ${data.failed} failed`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendNudge = async (id) => {
    try {
      const { data } = await api.post(`/orders/${id}/nudge`);
      toast.success(`Reminder emailed to ${data.to}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const followUpWhatsapp = (o) => {
    const num = (o.user_phone || "").replace(/\D/g, "");
    if (!num) return toast.error("This order has no phone number");
    const msg =
      `Namaste ${o.user_name}! This is Immortal Vedas about your order #${o.id.slice(0, 8)} ` +
      `(₹${o.total?.toFixed(2)}) — the payment did not complete.\n\n` +
      `${o.items?.map((i) => `• ${i.name} x${i.quantity}`).join("\n")}\n\n` +
      `You can finish the payment here: ${window.location.origin}/track\n` +
      `Reply to this message if you'd like us to help.`;
    return window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center mb-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs capitalize transition-all ${
              filter === f ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary/40"}`}
            data-testid={`order-filter-${f}`}
          >
            {f === "review" ? "Needs review" : f} ({counts[f] || 0})
          </button>
        ))}
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="order-source-filter">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {sources.map((s) => <SelectItem key={s} value={s}>{sourceLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="btn-pill ml-auto" onClick={retryFailedInvoices} data-testid="adm-retry-invoices">
          <Mail size={13} className="mr-1.5"/> Retry Failed Invoices
        </Button>
      </div>

      {visible.map((o) => (
        <Card key={o.id} className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-medium">Order #{o.id.slice(0, 8)} — {o.user_name}</div>
                <div className="text-xs text-muted-foreground">
                  {o.user_email} · {new Date(o.created_at).toLocaleString()}
                  {o.invoice_number ? ` · ${o.invoice_number}` : ""}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <Badge variant="outline" className="text-[10px]" data-testid={`adm-order-src-${o.id}`}>
                    {sourceLabel(o.src)}
                  </Badge>
                  {(o.ref || o.referral_src) && (
                    <Badge variant="outline" className="text-[10px] border-primary/30 text-primary bg-primary/5"
                           data-testid={`adm-order-ref-${o.id}`}>
                      {o.ref || o.referral_src}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl">₹{o.total?.toFixed(2)}</div>
                <div className="flex items-center gap-1 justify-end mt-1">
                  <PaymentBadge status={o.payment_status} needsReview={o.needs_review} testid={`adm-pay-badge-${o.id}`}/>
                  {o.payment_status !== "paid" && o.nudge_sent && (
                    <Badge variant="outline" className="text-[10px] border-primary/40 text-primary bg-primary/10" data-testid={`adm-nudge-sent-${o.id}`}>
                      Reminder sent
                    </Badge>
                  )}
                  {o.invoice_emailed === false && o.payment_status === "paid" && (
                    <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-700 bg-amber-500/10" data-testid={`adm-invoice-unsent-${o.id}`}>
                      Invoice not emailed
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">{o.items?.map((i) => `${i.name}×${i.quantity}`).join(", ")}</div>
            <div className="text-xs mt-1">{o.address}</div>
            {o.failure_reason && (
              <div className="text-xs text-destructive mt-1" data-testid={`adm-fail-reason-${o.id}`}>Gateway: {o.failure_reason}</div>
            )}
            <button
              onClick={() => toggle(o.id)}
              className="mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              data-testid={`adm-order-expand-${o.id}`}
            >
              {expanded[o.id] ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
              {expanded[o.id] ? "Hide details" : "Order details"}
            </button>
            {expanded[o.id] && <OrderRowDetails order={o} />}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                <SelectTrigger className="w-44" data-testid={`order-status-${o.id}`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              {o.payment_status === "paid" && (
                <Button variant="outline" size="sm" className="btn-pill" onClick={() => window.open(`${API}/orders/${o.id}/invoice`, "_blank")} data-testid={`adm-invoice-${o.id}`}>
                  <FileDown size={13} className="mr-1.5"/> Invoice
                </Button>
              )}
              {o.payment_status === "paid" && o.invoice_emailed === false && (
                <Button variant="outline" size="sm" className="btn-pill border-secondary/40 text-secondary" onClick={() => resendInvoice(o.id)} data-testid={`adm-resend-invoice-${o.id}`}>
                  <Mail size={13} className="mr-1.5"/> Email Invoice
                </Button>
              )}
              {o.payment_status !== "paid" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="btn-pill"
                  onClick={() => sendNudge(o.id)}
                  data-testid={`adm-nudge-${o.id}`}
                >
                  <BellRing size={13} className="mr-1.5"/> {o.nudge_sent ? "Send reminder again" : "Email reminder"}
                </Button>
              )}
              {o.payment_status !== "paid" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="btn-pill border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                  onClick={() => followUpWhatsapp(o)}
                  data-testid={`adm-followup-${o.id}`}
                >
                  <MessageCircle size={13} className="mr-1.5"/> Follow up
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default OrdersTab;
